import React, { useState, useRef } from 'react';
import { Box, Button, Typography, CircularProgress, useTheme } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { readXlsx, parseExcel, downloadExcelTemplate, downloadErrorExcel, cellToString } from './ExcelParser';
import BulkPreviewTable from './BulkPreviewTable';
import BulkResultSummary from './BulkResultSummary';
import { MembershipManagmentB2BService } from '@sdk-point/talisis';
import Toast from '../../../../../design-system/components/Toast/Toast';
import { useB2BPermissions } from '../../../../../context/B2BPermissionsContext';

const MembershipManagmentB2BServiceSDK = new MembershipManagmentB2BService(
    process.env.REACT_APP_ENV_V2,
    process.env.REACT_APP_X_API_KEY_V2
);

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

// Email verification concurrency: keeps pressure on the service bounded.
// 10 workers × ~200ms avg = ~20s for 1000 unique emails.
const EMAIL_CHECK_CONCURRENCY = 10;

/**
 * Extract the month count from a plazo/period label.
 * Handles the file format ("1 MES", "3 MESES", "12 meses") and the catalog
 * `name` format ("Membresía Lite (1 mes)", "Membresía Lite (12 meses)").
 * Returns a number (1, 3, 6, 12, ...) or null if none is found.
 */
const extractMonths = (label) => {
    const match = String(label == null ? '' : label).match(/(\d+)\s*mes/i);
    return match ? parseInt(match[1], 10) : null;
};

/**
 * Extract the program id from a `programa` cell.
 * The template now ships the program as a descriptive label with the id
 * embedded, e.g. "UNID-Licenciatura En Contabilidad Y Finanzas (id: 700)".
 * We validate against the catalog by this id (same identity the manual form
 * uses), not by the label text. Returns the id as a string, or null if the
 * "(id: NNN)" marker is absent.
 */
const extractProgramId = (label) => {
    const match = String(label == null ? '' : label).match(/\(\s*id\s*:\s*(\d+)\s*\)/i);
    return match ? match[1] : null;
};

const BulkUploadPage = ({ companySelected, groupSelected, companyCatalog, sendEmail, emailSelected, onCompanyResolved }) => {
    const user = useSelector((state) => state.userReducer.user);
    const { assignedCompanyName } = useB2BPermissions();
    const fileInputRef = useRef(null);
    const theme = useTheme();
    const primaryColor = theme.palette.primary.main;

    // Company and group always come from the on-screen selectors, never from the file.
    const companyId = companySelected?.id || '';
    const groupId = groupSelected?.id || '';
    const hasSelection = Boolean(companyId && groupId);

    // UX guard only. The backend MUST re-validate the caller's scope on every row.
    // The company selector is already restricted to the user's configured
    // companies, so a selected company should always be in scope; this validates
    // it by name (normalized) against the user's scope as a safety net. If the
    // user has no configured companies, nothing is in scope.
    const normalizeCompanyName = (name) => (name || '').toString().trim().toUpperCase();
    const assignedNameSet = new Set((assignedCompanyName || []).map(normalizeCompanyName));
    const isCompanyInScope =
        Boolean(companyId) && assignedNameSet.has(normalizeCompanyName(companySelected?.label));

    const [parsedData, setParsedData] = useState(null);
    const [validationResult, setValidationResult] = useState(null);
    const [loading, setLoading] = useState(false);
    // Resolved IDs: maps description (from the Excel file) -> internal id/sku for postCreateUsers.
    const [resolvedIds, setResolvedIds] = useState({});
    const [progress, setProgress] = useState('');
    // Dedicated flag for the "reading/parsing file" phase, kept separate from
    // `loading` (validation/processing) so it never interferes with the company
    // auto-resolution flow.
    const [fileLoading, setFileLoading] = useState(false);
    const [step, setStep] = useState('upload'); // 'upload' | 'preview' | 'validated' | 'processed'
    const [toastProps, setToastProps] = useState({ open: false, message: '', severity: 'success' });

    const handleFileSelect = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate extension
        if (!file.name.toLowerCase().endsWith('.xlsx')) {
            setToastProps({ open: true, message: 'Solo se permiten archivos .xlsx', severity: 'error' });
            return;
        }
        // Validate size
        if (file.size > MAX_FILE_SIZE) {
            setToastProps({ open: true, message: 'El archivo excede el tamaño máximo de 5MB', severity: 'error' });
            return;
        }

        setFileLoading(true);
        try {
            // readXlsx (ExcelParser) unzips the .xlsx with jszip and parses its
            // XML with DOMParser, returning a matrix of rows (each row = array of
            // string cells). No external xlsx library is required.
            let sheet;
            try {
                sheet = await readXlsx(file);
            } catch (err) {
                setToastProps({
                    open: true,
                    message: 'No se pudo leer el archivo. Verifique que sea un .xlsx válido.',
                    severity: 'error',
                });
                return;
            }

            // Resolve and VALIDATE the company from the file BEFORE anything else.
            // This is the FIRST hard gate: if the company is missing, empty, or not
            // in the catalog, we stop here and never reach the preview/validate step.
            let resolvedCompanyLabel = companySelected?.label;

            // Normalize for matching: lowercase, no accents, trimmed.
            const normalize = (s) => String(s == null ? '' : s).trim().toLowerCase()
                .normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ');

            // The catalog MUST be loaded to validate. If it isn't, block instead of
            // silently skipping the check (that let unknown companies slip through).
            if (!companyCatalog || companyCatalog.length === 0) {
                setToastProps({
                    open: true,
                    message: 'El catálogo de empresas aún no está disponible. Espere unos segundos e intente de nuevo.',
                    severity: 'error',
                });
                return;
            }

            if (Array.isArray(sheet) && sheet.length >= 2) {
                const headers = (sheet[0] || []).map(h => cellToString(h).toLowerCase());
                const empresaIdx = headers.indexOf('empresa');

                if (empresaIdx < 0) {
                    setToastProps({
                        open: true,
                        message: 'El archivo no contiene la columna "empresa".',
                        severity: 'error',
                    });
                    return;
                }

                // Validate the `empresa` column on EVERY data row against the catalog.
                // If ANY row has a missing or unknown company, we collect all the
                // failures and block until the whole file is corrected.
                const companyErrors = [];
                for (let i = 1; i < sheet.length; i++) {
                    const cells = sheet[i] || [];
                    const isEmpty = cells.every(c => cellToString(c) === '');
                    if (isEmpty) continue; // skip empty rows
                    const rowEmpresa = cellToString(cells[empresaIdx]);

                    if (!rowEmpresa) {
                        companyErrors.push(`Fila ${i}: la columna "empresa" está vacía`);
                        continue;
                    }
                    const rowMatch = companyCatalog.find(c =>
                        normalize(c.label) === normalize(rowEmpresa)
                    );
                    if (!rowMatch) {
                        companyErrors.push(`Fila ${i}: la empresa "${rowEmpresa}" no existe en el catálogo`);
                    }
                }

                // Resolve the company from the first data row (best-effort) to
                // auto-fill the on-screen selector, even if some rows have errors.
                const firstRowEmpresa = cellToString((sheet[1] || [])[empresaIdx]);
                const match = companyCatalog.find(c =>
                    normalize(c.label) === normalize(firstRowEmpresa)
                );
                if (match) {
                    if (onCompanyResolved) {
                        await onCompanyResolved(match);
                    }
                    resolvedCompanyLabel = match.label;
                }

                // Parse the file so we can ALWAYS show the detected rows in the
                // preview, then merge company errors with format errors.
                const result = parseExcel(sheet, {
                    companyName: resolvedCompanyLabel,
                    groupName: groupSelected?.label,
                });
                const allErrors = [...companyErrors, ...result.errors];
                setParsedData({ ...result, errors: allErrors });

                // Show the preview table with the data. If there are errors, they
                // are rendered in the panel and the validate button stays blocked.
                if (result.rows.length > 0) {
                    setStep('preview');
                }
                return;
            }

            const result = parseExcel(sheet, {
                companyName: resolvedCompanyLabel,
                groupName: groupSelected?.label,
            });
            setParsedData(result);

            if (result.rows.length > 0) {
                setStep('preview');
            }
        } finally {
            setFileLoading(false);
        }
    };

    /**
     * Verifies emails against the platform using `postVerifyEmail`.
     * De-duplicates so each email is checked only once regardless of how many
     * rows share it. 10 concurrent workers keep it bounded for large files.
     *
     * @returns {Promise<Map<number, string[]>>} row_number -> error messages
     */
    const validateEmails = async () => {
        const rowErrors = new Map();
        const addError = (rowNumber, message) => {
            if (!rowErrors.has(rowNumber)) rowErrors.set(rowNumber, []);
            rowErrors.get(rowNumber).push(message);
        };

        // De-duplicate: only one request per distinct email address.
        const emailToRows = new Map();
        parsedData.rows.forEach(row => {
            if (!row.correo) return;
            const key = row.correo.trim().toLowerCase();
            if (!emailToRows.has(key)) emailToRows.set(key, []);
            emailToRows.get(key).push(row.row_number);
        });

        const uniqueEmails = [...emailToRows.keys()];
        const total = uniqueEmails.length;
        let checked = 0;
        const results = new Map();

        const worker = async () => {
            for (;;) {
                const email = uniqueEmails.shift();
                if (!email) return;
                try {
                    const response = await MembershipManagmentB2BServiceSDK.postVerifyEmail({ email });
                    results.set(email, { exists: response?.exists ?? false, ok: !!response?.success });
                } catch (err) {
                    results.set(email, { exists: false, ok: false });
                }
                checked += 1;
                setProgress(`Verificando correos (${checked}/${total})`);
            }
        };

        await Promise.all(
            Array.from({ length: Math.min(EMAIL_CHECK_CONCURRENCY, uniqueEmails.length) }, worker)
        );

        results.forEach((result, email) => {
            const rows = emailToRows.get(email) || [];
            rows.forEach(rowNumber => {
                if (!result.ok) {
                    addError(rowNumber, `no se pudo verificar el correo "${email}", intente de nuevo`);
                } else if (result.exists) {
                    addError(rowNumber, `el correo "${email}" ya está registrado`);
                }
            });
        });

        return rowErrors;
    };

    /**
     * Loads every catalog the file needs and validates each row against them.
     *
     * Plazo (period) and programa are CASCADING catalogs: they depend on the
     * membership, so they are fetched once per distinct `membresia_sku` in the
     * file, not once per row.
     *
     * @returns {Promise<{ rowErrors: Map<number, string[]>, labels: object }>}
     */
    const validateAgainstCatalogs = async () => {
        const rowErrors = new Map();
        const labels = {};
        const addError = (rowNumber, message) => {
            if (!rowErrors.has(rowNumber)) rowErrors.set(rowNumber, []);
            rowErrors.get(rowNumber).push(message);
        };

        // Flat catalogs: membership and addon do not depend on anything else.
        const [membershipData, addonData] = await Promise.all([
            MembershipManagmentB2BServiceSDK.getCatalogMembership(),
            MembershipManagmentB2BServiceSDK.getCatalogAddon(),
        ]);

        const memberships = new Map(); // description (normalized) -> { id, description }
        if (membershipData?.success) {
            membershipData.data.forEach(item => {
                const key = String(item.description || '').trim().toLowerCase()
                    .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                memberships.set(key, { id: String(item.id), description: item.description });
                labels[key] = item.description;
            });
        }

        const addons = new Map(); // sku -> { sku, name }
        if (addonData?.success) {
            addonData.data.forEach(item => {
                const key = String(item.sku || '').trim().toLowerCase();
                addons.set(key, { sku: String(item.sku), name: item.name });
            });
        }

        // Cascading catalogs, one fetch per distinct membership referenced.
        const referencedMemberships = [
            ...new Set(parsedData.rows.map(r => String(r.membresia_sku || '').trim().toLowerCase()
                .normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\uFFFD/g, '')).filter(Boolean)),
        ].filter(key => memberships.has(key));

        const periodsByMembership = new Map(); // membershipKey -> Map<sku_normalized, { sku, name }>
        const programsByIdByMembership = new Map(); // membershipKey -> Map<program_id_string, { id, program }>

        await Promise.all(
            referencedMemberships.map(async membershipKey => {
                const { id: membershipId } = memberships.get(membershipKey);
                const [periodData, programData] = await Promise.all([
                    MembershipManagmentB2BServiceSDK.getCatalogPeriod({ id: membershipId }),
                    MembershipManagmentB2BServiceSDK.getCatalogPrograms({ id: membershipId }),
                ]);

                // Periods are keyed by their month count (1, 3, 6, 12), extracted
                // from the catalog `name` (e.g. "Membresía Lite (1 mes)" -> 1).
                // The file provides the plazo as a human label ("1 MES"), so the
                // real period SKU (e.g. "STA-MEM-LIT-1M") is RESOLVED from the
                // catalog by month count instead of being typed or built by hand.
                const periods = new Map(); // months (number) -> { sku, name }
                if (periodData?.success) {
                    periodData.data.forEach(item => {
                        const months = extractMonths(item.name);
                        if (months != null) {
                            periods.set(months, { sku: String(item.sku), name: item.name });
                        }
                    });
                }
                periodsByMembership.set(membershipKey, periods);

                // Index programs by their numeric id — the only identity we
                // trust from the file (the label text may be misspelled).
                const programsById = new Map(); // program id (string) -> { id, program }
                if (programData?.success) {
                    programData.data.forEach(item => {
                        programsById.set(String(item.id), { id: String(item.id), program: item.program });
                    });
                }
                programsByIdByMembership.set(membershipKey, programsById);
            })
        );

        // Row-level checks + build resolution map (description -> id/sku).
        const resolution = {}; // { [row_number]: { membershipId, plazoSku, addonSku, programId } }

        parsedData.rows.forEach(row => {
            const membershipKey = String(row.membresia_sku || '').trim().toLowerCase()
                .normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\uFFFD/g, '');
            const rowRes = { membershipId: '', plazoSku: '', addonSku: '', programId: '' };

            // membresia (label) vs membership_cat.description
            if (!memberships.has(membershipKey)) {
                addError(row.row_number, `la membresía "${row.membresia_sku}" no existe en el catálogo`);
                resolution[row.row_number] = rowRes;
                return;
            }

            const membership = memberships.get(membershipKey);
            rowRes.membershipId = membership.id;

            // plazo: the file gives a human label ("1 MES"); resolve the real
            // period SKU by matching its month count against membership_period.
            const periods = periodsByMembership.get(membershipKey) || new Map();
            const plazoMonths = extractMonths(row.plazo_sku);
            if (plazoMonths == null) {
                addError(
                    row.row_number,
                    `plazo "${row.plazo_sku}" no tiene un número de meses válido (ej. "1 MES", "3 MESES")`
                );
            } else if (!periods.has(plazoMonths)) {
                addError(
                    row.row_number,
                    `plazo "${row.plazo_sku}" no es válido para la membresía "${membership.description}"`
                );
            } else {
                rowRes.plazoSku = periods.get(plazoMonths).sku;
            }

            // programa vs products_by_membership. The template ships the program
            // as "Nombre Del Programa (id: NNN)". We validate ONLY by that id —
            // the label text is NOT trusted (it may be misspelled in the file),
            // so the id is the single source of truth, matched against the
            // programs catalog of THIS membership (same chained rule as plazo).
            // Program is optional: skip when the cell is empty.
            if (row.programa_id) {
                const programId = extractProgramId(row.programa_id);
                const programsById = programsByIdByMembership.get(membershipKey) || new Map();
                if (programsById.size === 0) {
                    // getCatalogPrograms returned no rows for this membership id:
                    // the membership has no programs configured. This is a catalog
                    // setup issue, not a bad id in the user's file — say so.
                    addError(
                        row.row_number,
                        `La membresía "${membership.description}" no tiene programas configurados`
                    );
                } else if (programId == null) {
                    addError(
                        row.row_number,
                        `programa "${row.programa_id}" no tiene un id válido; use el formato "Nombre del programa (id: 123)"`
                    );
                } else if (!programsById.has(programId)) {
                    addError(
                        row.row_number,
                        `el programa con id ${programId} no está dentro de la membresía "${membership.description}"`
                    );
                } else {
                    rowRes.programId = programsById.get(programId).id;
                }
            }

            // addon_sku vs addon_productos.sku
            if (row.addon_sku) {
                const addonKey = String(row.addon_sku).trim().toLowerCase();
                if (!addons.has(addonKey)) {
                    addError(row.row_number, `addon_sku "${row.addon_sku}" no existe en el catálogo`);
                } else {
                    rowRes.addonSku = addons.get(addonKey).sku;
                }
            }

            resolution[row.row_number] = rowRes;
        });

        return { rowErrors, resolution };
    };

    const handleValidate = async () => {
        if (!parsedData || parsedData.rows.length === 0) return;

        // Validate the required on-screen selections ONE BY ONE, so the user knows
        // exactly which field is missing.
        if (!companyId) {
            setToastProps({ open: true, message: 'Seleccione una empresa.', severity: 'error' });
            return;
        }
        if (!isCompanyInScope) {
            setToastProps({ open: true, message: 'La empresa seleccionada no está dentro de sus empresas asignadas.', severity: 'error' });
            return;
        }
        if (!groupId) {
            setToastProps({ open: true, message: 'Seleccione un grupo.', severity: 'error' });
            return;
        }
        // "enviar correo" MUST be chosen (Sí/No). The actual send is forced to false
        // in the payload during testing (see handleProcess), but the selection is
        // still required here.
        if (emailSelected === '' || emailSelected === undefined || emailSelected === null) {
            setToastProps({ open: true, message: 'Seleccione si desea enviar correo.', severity: 'error' });
            return;
        }

        setLoading(true);
        try {
            // Local validation: catalog codes are checked client-side against the
            // same catalogs the manual form uses, and email existence is verified
            // via postVerifyEmail. Valid rows are then sent to postCreateUsers.
            setProgress('Validando catálogos...');
            const { rowErrors, resolution } = await validateAgainstCatalogs();
            setResolvedIds(resolution);

            setProgress('Verificando correos...');
            const emailErrors = await validateEmails();

            const validRows = [];
            const invalidRows = [];
            parsedData.rows.forEach(row => {
                const errors = [
                    ...(rowErrors.get(row.row_number) || []),
                    ...(emailErrors.get(row.row_number) || []),
                ];
                if (errors.length > 0) {
                    invalidRows.push({ row: row.row_number, message: errors.join(' | ') });
                } else {
                    validRows.push({ row: row.row_number });
                }
            });

            setValidationResult({
                valid: validRows.length,
                invalid: invalidRows.length,
                results: { valid_rows: validRows, invalid_rows: invalidRows },
            });
            setStep('validated');
        } catch (err) {
            setToastProps({
                open: true,
                message: err?.response?.data?.message || 'Error al validar el archivo',
                severity: 'error',
            });
        } finally {
            setLoading(false);
            setProgress('');
        }
    };

    const handleProcess = async () => {
        if (!validationResult || !validationResult.results?.valid_rows) return;
        if (!hasSelection || !isCompanyInScope) {
            setToastProps({
                open: true,
                message: 'Seleccione una empresa y un grupo válidos antes de procesar',
                severity: 'error',
            });
            return;
        }
        setLoading(true);
        try {
            const validRows = validationResult.results.valid_rows;
            // Transform to postCreateUsers payload
            const payload = {
                // TESTING: emails are NOT sent for now. Restore `sendEmail || false`
                // (the user's selection) once bulk email sending is enabled.
                sendEmail: false,
                userPersonId: user.person_id,
                users: parsedData.rows
                    .filter(row => validRows.some(vr => vr.row === row.row_number))
                    .map(row => {
                        // Use the SKUs/ids RESOLVED during validation, not the raw
                        // file values. `membership` must be the real period SKU
                        // (e.g. "STA-MEM-LIT-1M") resolved from the catalog by
                        // month count, never the human label ("1 MES").
                        const resolved = resolvedIds[row.row_number] || {};
                        return {
                            firstName: row.nombre,
                            lastName: row.apellidos,
                            emailAddress: row.correo,
                            phone: '',
                            tags: {
                                company: companyId,
                                group: groupId,
                                attribute1: row.tag1 || '',
                                attribute2: row.tag2 || '',
                                attribute3: row.tag3 || '',
                            },
                            membership: resolved.plazoSku || '',
                            addon: resolved.addonSku || '',
                            paymentReferenceId: `B2B-${companySelected?.tag?.split('=')[1] || 'EMPRESA'}`,
                            program: resolved.programId || '',
                        };
                    }),
            };

            await MembershipManagmentB2BServiceSDK.postCreateUsers(payload);
            setStep('processed');
            setToastProps({ open: true, message: 'Se envió a proceso de altas', severity: 'success' });
        } catch (err) {
            setToastProps({
                open: true,
                message: err?.response?.data?.message || 'Error al procesar',
                severity: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadTemplate = async () => {
        try {
            await downloadExcelTemplate('plantilla_carga_masiva_b2b.xlsx');
        } catch (err) {
            setToastProps({ open: true, message: 'No se pudo generar la plantilla', severity: 'error' });
        }
    };

    const handleDownloadErrors = async () => {
        if (!validationResult?.results?.invalid_rows) return;
        const headers = parsedData.headers;
        const errorRows = validationResult.results.invalid_rows.map(ir => {
            const originalRow = parsedData.rows.find(r => r.row_number === ir.row) || {};
            return { ...originalRow, error: ir.message };
        });
        try {
            await downloadErrorExcel(headers, errorRows, 'errores_carga_masiva.xlsx');
        } catch (err) {
            setToastProps({ open: true, message: 'No se pudo generar el archivo de errores', severity: 'error' });
        }
    };

    const handleReset = () => {
        setParsedData(null);
        setValidationResult(null);
        setResolvedIds({});
        setStep('upload');
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleFinishedToast = () => {
        setToastProps(prev => ({ ...prev, open: false }));
    };

    return (
        <Box>
            {/* Template download + reset */}
            <Box display="flex" alignItems="center" mb={2} style={{ gap: 16 }}>
                <Button variant="contained" color="primary" size="small" onClick={handleDownloadTemplate}>
                    📥 Descargar Plantilla Excel
                </Button>
                {step !== 'upload' && (
                    <Button variant="text" size="small" onClick={handleReset}>
                        Reiniciar
                    </Button>
                )}
            </Box>

            {/* Upload area */}
            {step === 'upload' && (
                <Box border={1} borderColor="grey.300" borderRadius={8} p={3} textAlign="center">
                    {isCompanyInScope ? (
                        <>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                                Seleccione un archivo Excel (.xlsx) con los datos de los alumnos (máx. 5MB)
                            </Typography>
                            {hasSelection && (
                                <Typography variant="caption" color="textSecondary" display="block">
                                    Todos los registros se darán de alta en{' '}
                                    <strong>{companySelected?.label}</strong> / <strong>{groupSelected?.label}</strong>
                                </Typography>
                            )}
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".xlsx"
                                onChange={handleFileSelect}
                                disabled={fileLoading}
                                style={{ marginTop: 16 }}
                            />
                            {fileLoading && (
                                <Box display="flex" alignItems="center" justifyContent="center" mt={3} style={{ gap: 10 }}>
                                    <CircularProgress size={20} thickness={5} color="primary" />
                                    <Typography variant="body2" style={{ color: primaryColor, fontWeight: 600 }}>
                                        Cargando archivo...
                                    </Typography>
                                </Box>
                            )}
                        </>
                    ) : (
                        <Typography variant="body2" color="error">
                            La empresa seleccionada no está dentro de sus empresas asignadas.
                        </Typography>
                    )}
                </Box>
            )}

            {/* Format errors */}
            {parsedData && parsedData.errors.length > 0 && (
                <Box mt={2} p={2} bgcolor="#fff3e0" borderRadius={4}>
                    <Typography variant="subtitle2" style={{ color: '#e65100' }}>
                        Errores de formato:
                    </Typography>
                    {parsedData.errors.map((err, i) => (
                        <Typography key={i} variant="body2" style={{ color: '#e65100' }}>
                            • {err}
                        </Typography>
                    ))}
                </Box>
            )}

            {/* Preview — stays visible after validation so codes can be read as descriptions */}
            {(step === 'preview' || step === 'validated') && parsedData && (
                <Box>
                    <BulkPreviewTable
                        rows={parsedData.rows}
                        companyLabel={companySelected?.label}
                        groupLabel={groupSelected?.label}
                        resolveLabel={(row, field) => row[field] || ''}
                    />
                    {step === 'preview' && (
                        <Box mt={2} display="flex" alignItems="center" style={{ gap: 12 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleValidate}
                                disabled={loading || parsedData.errors.length > 0}
                                startIcon={loading ? <CircularProgress size={16} /> : null}
                            >
                                {loading ? 'Validando...' : 'Validar Archivo'}
                            </Button>
                            {parsedData.errors.length > 0 && (
                                <Button variant="contained" color="primary" size="small" onClick={handleReset}>
                                    Subir archivo corregido
                                </Button>
                            )}
                        </Box>
                    )}
                </Box>
            )}

            {/* Validation result */}
            {step === 'validated' && validationResult && (
                <Box>
                    <BulkResultSummary
                        validCount={validationResult.valid || 0}
                        invalidCount={validationResult.invalid || 0}
                        invalidRows={validationResult.results?.invalid_rows || []}
                        onDownloadErrors={handleDownloadErrors}
                    />
                    {(validationResult.valid || 0) > 0 && (
                        <Box mt={2}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleProcess}
                                disabled={loading}
                                startIcon={loading ? <CircularProgress size={16} /> : null}
                            >
                                {loading ? 'Procesando...' : `Crear ${validationResult.valid} alta(s)`}
                            </Button>
                        </Box>
                    )}
                    {(validationResult.invalid || 0) > 0 && (
                        <Box mt={1}>
                            <Typography variant="body2" color="textSecondary">
                                Corrija los errores, descargue el Excel de errores y re-suba el archivo corregido.
                            </Typography>
                            <Box mt={1}>
                                <input
                                    type="file"
                                    accept=".xlsx"
                                    onChange={(e) => {
                                        handleFileSelect(e);
                                        setValidationResult(null);
                                    }}
                                />
                            </Box>
                        </Box>
                    )}
                </Box>
            )}

            {/* Process result */}
            {step === 'processed' && (
                <Box mt={2} p={2} bgcolor="#e8f5e9" borderRadius={4}>
                    <Typography variant="subtitle1" style={{ color: '#2e7d32', fontWeight: 600 }}>
                        Se envió a proceso de altas
                    </Typography>
                    <Typography variant="body2">
                        Los registros válidos se enviaron a proceso de altas.
                    </Typography>
                    <Box mt={2}>
                        <Button variant="contained" color="primary" onClick={handleReset}>
                            Cargar otro archivo
                        </Button>
                    </Box>
                </Box>
            )}

            <Toast
                severity={toastProps.severity}
                open={toastProps.open}
                message={toastProps.message}
                onFinished={handleFinishedToast}
                duration={5000}
            />
        </Box>
    );
};

export default BulkUploadPage;
